import { useEffect } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Learn how Mystic Forest protects your privacy and handles your personal data in compliance with EU GDPR regulations."
        canonicalUrl="https://mysticforest.club/privacy-policy"
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="pt-32 pb-24">
          <div className="max-w-4xl mx-auto px-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>

            <h1 className="text-4xl md:text-5xl font-serif font-light mb-8 text-foreground">
              Privacy Policy
            </h1>
            
            <div className="prose prose-lg max-w-none space-y-8">
              <p className="text-muted-foreground">
                Last updated: January 26, 2025
              </p>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Mystic Forest ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal data. 
                  This privacy policy explains how we collect, use, and safeguard your information when you visit our website 
                  mysticforest.club (the "Site") or use our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">2. Data We Collect</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We collect the following types of personal data:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Contact Information:</strong> Name, email address, phone number (when you submit forms or sign up for our mailing list)</li>
                  <li><strong>Usage Data:</strong> IP address, browser type, device information, pages visited, time spent on pages (via Google Analytics)</li>
                  <li><strong>Cookies:</strong> We use essential cookies for site functionality and optional analytics cookies</li>
                  <li><strong>Event Registration Data:</strong> Information you provide when booking retreats or workshops</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">3. How We Use Your Data</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use your personal data for the following purposes:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li>To process your event registrations and communicate with you about upcoming experiences</li>
                  <li>To send you our newsletter and updates (only if you've opted in)</li>
                  <li>To improve our website and services through analytics</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">4. Legal Basis for Processing (EU GDPR)</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Under the EU General Data Protection Regulation (GDPR), we process your data based on:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Consent:</strong> When you opt in to our mailing list or accept cookies</li>
                  <li><strong>Contract:</strong> When necessary to fulfill services you've requested (e.g., event registration)</li>
                  <li><strong>Legitimate Interests:</strong> For analytics and improving our services, where it doesn't override your rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">5. Data Sharing and Third Parties</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We do not sell your personal data. We may share your data with:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Supabase:</strong> Our database and authentication provider (data stored in EU)</li>
                  <li><strong>Google Analytics:</strong> For website analytics (with IP anonymization)</li>
                  <li><strong>Email Service Provider:</strong> To send newsletters and transactional emails</li>
                  <li><strong>Payment Processors:</strong> If you make purchases (when shop is live)</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  All third-party service providers are carefully selected and required to protect your data in compliance with GDPR.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">6. Data Retention</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We retain your personal data only as long as necessary for the purposes outlined in this policy:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground mt-4">
                  <li>Mailing list data: Until you unsubscribe</li>
                  <li>Event registration data: Up to 3 years after the event</li>
                  <li>Analytics data: Aggregated data retained for up to 26 months</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">7. Your Rights (GDPR)</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Under GDPR, you have the right to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Access:</strong> Request a copy of your personal data</li>
                  <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                  <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                  <li><strong>Restriction:</strong> Limit how we use your data</li>
                  <li><strong>Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                  <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent at any time (without affecting prior lawful processing)</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  To exercise any of these rights, please contact us at{' '}
                  <a href="mailto:hello@mysticforest.club" className="text-accent hover:underline">
                    hello@mysticforest.club
                  </a>
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">8. Cookies</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use the following types of cookies:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                  <li><strong>Essential Cookies:</strong> Necessary for site functionality (e.g., session management)</li>
                  <li><strong>Analytics Cookies:</strong> Google Analytics to understand site usage (only with your consent)</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  You can manage cookie preferences through our cookie banner or your browser settings.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">9. Data Security</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized 
                  access, loss, or misuse. This includes encryption (HTTPS), secure databases, and regular security reviews.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">10. International Data Transfers</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Your data is primarily stored within the European Union. If data is transferred outside the EU/EEA, 
                  we ensure appropriate safeguards are in place (such as Standard Contractual Clauses).
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">11. Children's Privacy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Our services are not directed to individuals under 16. We do not knowingly collect personal data from children. 
                  If you believe we have collected data from a child, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">12. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this privacy policy from time to time. We will notify you of significant changes by 
                  posting the new policy on this page with an updated "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">13. Contact Us</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you have any questions about this privacy policy or our data practices, please contact us:
                </p>
                <div className="mt-4 text-muted-foreground">
                  <p>Email: <a href="mailto:hello@mysticforest.club" className="text-accent hover:underline">hello@mysticforest.club</a></p>
                  <p className="mt-2">Mystic Forest<br />Poland</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-serif font-medium mb-4 text-foreground">14. Supervisory Authority</h2>
                <p className="text-muted-foreground leading-relaxed">
                  If you are located in the EU/EEA and have concerns about our data practices, you have the right to 
                  lodge a complaint with your local data protection authority.
                </p>
              </section>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy;
